import { useController } from '@framework/form/hooks/useController.ts';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { TextAreaInput } from '@framework/form/inputs/TextAreaInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Datatype } from '../../../../expressions/datatype/Datatype.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { WidgetType } from '../../../Widget.ts';
import { WidgetConfigurationSlotRender } from '../../common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '../../common/config-slots/WidgetConfigurationSlots.tsx';
import { DefaultValueInput } from '../../common/DefaultValueInput.tsx';
import { CodeLabelInputForm } from '../common/CodeLabelInputForm.tsx';
import { CommonInputWidgetConfigSchema } from '../common/CommonInputConfigComponent.tsx';
import { HiddenWidgetType } from './HiddenWidget.type.tsx';
import { type IHiddenWidget } from './IHiddenWidget.ts';

export const HiddenWidgetConfigSchema: yup.ObjectSchema<IHiddenWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetType.Hidden).required(),
    dataType: yup
      .string()
      .oneOf([Datatype.String, Datatype.Number, Datatype.Boolean])
      .required()
      .default(Datatype.String)
      .label('Tipo di dato'),
    notes: yup.string().notRequired().label('Note'),
  });

export interface IHiddenWidgetConfigComponentProps
  extends IWidgetComponentProps<IHiddenWidget> {}

export function HiddenWidgetConfigComponent(
  props: IHiddenWidgetConfigComponentProps,
) {
  const widgetConfigForm = useWatch() as IHiddenWidget;

  const { field: dataTypeField } = useController('dataType');

  const fieldDataType = useMemo(() => {
    return HiddenWidgetType.getReference?.([], widgetConfigForm)?.field;
  }, [widgetConfigForm]);

  return (
    <>
      <CodeLabelInputForm hideLabel hideColSpan />
      <FormItem name="dataType" label="Tipo di dato">
        <SelectInput
          name="dataType"
          defaultValue={Datatype.String}
          options={[
            {
              label: 'Stringa',
              value: Datatype.String,
            },
            {
              label: 'Numero',
              value: Datatype.Number,
            },
            {
              label: 'Booleano',
              value: Datatype.Boolean,
            },
          ]}
        />
      </FormItem>
      {fieldDataType && (
        <WidgetConfigurationSlotRender
          slot={WidgetConfigurationSlots.Default}
          order={10}
        >
          <DefaultValueInput
            allowsReferences={ref => {
              return ref.field.type === dataTypeField.value;
            }}
            forceFieldDataType={fieldDataType}
          />
        </WidgetConfigurationSlotRender>
      )}
      <FormItem
        name="notes"
        label="Note del configuratore"
        help="Questo campo sarà visibile solo in fase di configurazione."
      >
        <TextAreaInput name={'notes'} rows={3} />
      </FormItem>
    </>
  );
}
